import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar as CalendarIcon, Check } from "lucide-react";
import { toast } from "sonner";
import {
  API,
  TIME_SLOTS,
  FORTY_DAYS_SLUG,
  getFortyDaysTimeSlots,
  FOURTH_TRIMESTER_SLUG,
  FOURTH_TRIMESTER_DATES,
  FOURTH_TRIMESTER_TIME_SLOTS,
  FOURTH_TRIMESTER_FIRST_DATE,
  FOURTH_TRIMESTER_LAST_DATE,
  COMPANION_SLUG,
  COMPANION_TIME_SLOTS,
  EMBRACE_SLUG,
  getEmbraceTimeSlots,
  MEALS_SLUG,
  MEALS_TIME_SLOTS,
  BELLY_SLUG,
  BELLY_TIME_SLOTS,
  PAYMENT_PLAN_SLUGS,
  isBookingDay,
  isFortyDaysBookingDay,
  isFourthTrimesterBookingDay,
  isCompanionBookingDay,
  isEmbraceBookingDay,
  isMealsBookingDay,
  isBellyBookingDay,
} from "@/data/site";
import { FadeUp } from "@/components/Reveal";
import { CourseDates } from "@/components/CourseDates";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const inputCls =
  "w-full rounded-lg border border-espresso/20 bg-transparent px-4 py-3 text-sm text-espresso placeholder:text-cocoa/50 focus:border-mauve focus:ring-1 focus:ring-mauve";

export default function Book() {
  const [params] = useSearchParams();
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [locked, setLocked] = useState(false);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [details, setDetails] = useState({ name: "", email: "", phone: "", notes: "" });
  const [taken, setTaken] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [calOpen, setCalOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/services`).then((res) => {
      setServices(res.data.filter((s) => !s.coming_soon));
      const pre = params.get("service");
      if (pre) {
        const found = res.data.find((s) => s.slug === pre);
        if (found) {
          setService(found);
          setLocked(true);
          setStep(found.slug === FOURTH_TRIMESTER_SLUG ? 3 : 2);
        }
      }
    }).catch(() => {});
  }, [params]);

  useEffect(() => {
    if (!date) return;
    setTime(null);
    axios
      .get(`${API}/availability`, { params: { date: format(date, "yyyy-MM-dd"), service: service?.slug } })
      .then((res) => setTaken(res.data.taken))
      .catch(() => setTaken([]));
  }, [date, service?.slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        service_slug: service.slug,
        service_title: service.title,
        date: isFourthTrimester ? FOURTH_TRIMESTER_DATES[0] : format(date, "yyyy-MM-dd"),
        time: isFourthTrimester ? FOURTH_TRIMESTER_TIME_SLOTS[0] : time,
        ...details,
      });
      setConfirmation(res.data);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Booking didn't go through — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isFortyDays = service?.slug === FORTY_DAYS_SLUG;
  const isFourthTrimester = service?.slug === FOURTH_TRIMESTER_SLUG;
  const isCompanion = service?.slug === COMPANION_SLUG;
  const isEmbrace = service?.slug === EMBRACE_SLUG;
  const isMeals = service?.slug === MEALS_SLUG;
  const isBelly = service?.slug === BELLY_SLUG;
  const dayFilter = isFourthTrimester
    ? isFourthTrimesterBookingDay
    : isFortyDays
      ? isFortyDaysBookingDay
      : isCompanion
        ? isCompanionBookingDay
        : isEmbrace
          ? isEmbraceBookingDay
          : isMeals
            ? isMealsBookingDay
            : isBelly
              ? isBellyBookingDay
              : isBookingDay;
  const activeSlots = isFourthTrimester
    ? FOURTH_TRIMESTER_TIME_SLOTS
    : isFortyDays
      ? getFortyDaysTimeSlots(date)
      : isCompanion
        ? COMPANION_TIME_SLOTS
        : isEmbrace
          ? getEmbraceTimeSlots(date)
          : isMeals
            ? MEALS_TIME_SLOTS
            : isBelly
              ? BELLY_TIME_SLOTS
              : TIME_SLOTS;
  const daysNote = isFourthTrimester
    ? "The 4th Trimester cohort gathers Sundays 3:00 – 5:00 PM (some classes may extend to 6:00 PM) for six weeks, beginning February 21, 2027."
    : isFortyDays
      ? "The First Forty Days runs on a fixed rhythm — Mondays & Wednesdays 10 AM–2 PM or 12–4 PM · Fridays 10 AM–1 PM."
      : isCompanion
        ? "Sacred Postpartum Companion visits are held Mondays and Wednesdays, 10 AM–2 PM or 12–4 PM."
        : isEmbrace
          ? "Mother's Embrace is held Tuesdays 11 AM–1 PM · Wednesdays 11 AM–4 PM · Fridays 12–2 PM."
          : isMeals
            ? "Nourishing Meal Preparation is held Mondays, Tuesdays and Wednesdays each week — plus one Saturday per month — from 11 AM – 4 PM."
            : isBelly
              ? "Belly Binding & Womb Healing is held Mondays, Tuesdays and Wednesdays, 11 AM – 4 PM. No weekends."
              : "Visits are held Mondays–Wednesdays and every other Saturday, 9am–5pm.";
  const calendarFromDate = isFourthTrimester ? FOURTH_TRIMESTER_FIRST_DATE : undefined;
  const calendarToDate = isFourthTrimester ? FOURTH_TRIMESTER_LAST_DATE : undefined;
  const wideSlots = isFortyDays || isFourthTrimester || isCompanion || isEmbrace || isMeals || isBelly;

  if (confirmation) {
    return (
      <main className="bg-ivory pt-40 pb-32 min-h-screen" data-testid="booking-confirmation">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <FadeUp>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sienna text-ivory">
              <Check className="h-7 w-7" strokeWidth={1.5} />
            </span>
            <h1 className="mt-8 font-serif text-5xl font-light text-espresso">
              You're <span className="italic text-sienna">booked in</span>
            </h1>
            <p className="mt-4 text-cocoa">
              {confirmation.booking.service_slug === FOURTH_TRIMESTER_SLUG
                ? "Your place in the six-week course is held. I'll be in touch by email within 24 hours."
                : "We'll confirm your visit by email within 24 hours."}
            </p>
            <div className="mt-10 rounded-2xl border border-espresso/15 bg-cream p-8 text-left" data-testid="booking-summary">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sienna">
                Reference · {confirmation.reference}
              </p>
              <p className="mt-4 font-serif text-2xl text-espresso">{confirmation.booking.service_title}</p>
              {confirmation.booking.service_slug === FOURTH_TRIMESTER_SLUG ? (
                <CourseDates />
              ) : (
                <p className="mt-2 text-sm text-cocoa">
                  {format(new Date(confirmation.booking.date + "T00:00:00"), "EEEE, MMMM d, yyyy")} at {confirmation.booking.time}
                </p>
              )}
              <p className="mt-1 text-sm text-cocoa">{confirmation.booking.name} · {confirmation.booking.email}</p>
            </div>
            <Link
              to="/"
              data-testid="confirmation-home-button"
              className="mt-10 inline-block rounded-full border border-espresso/30 px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-espresso transition-colors hover:bg-espresso hover:text-ivory"
            >
              Back home
            </Link>
          </FadeUp>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-ivory pt-32 pb-24 lg:pt-40" data-testid="booking-page">
      <div className="mx-auto max-w-4xl px-6">
        <FadeUp>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sienna">Book a visit</p>
          <h1 className="mt-5 font-serif text-5xl font-light leading-tight text-espresso lg:text-6xl">
            Begin your <span className="italic text-sienna">care</span>
          </h1>
        </FadeUp>

        <div className="mt-10 flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.2em]" data-testid="booking-steps">
          {(isFourthTrimester
            ? locked ? [[3, "Details"]] : [[1, "Service"], [3, "Details"]]
            : locked ? [[2, "Date & time"], [3, "Details"]] : [[1, "Service"], [2, "Date & time"], [3, "Details"]]
          ).map(([stepNum, label], i, arr) => {
            return (
              <div key={label} className="flex items-center gap-4">
                <span className={step === stepNum ? "text-sienna" : step > stepNum ? "text-espresso" : "text-cocoa/40"}>
                  {String(i + 1).padStart(2, "0")} · {label}
                </span>
                {i < arr.length - 1 && <span className="h-px w-8 bg-espresso/20" />}
              </div>
            );
          })}
        </div>

        {locked && service && (step === 2 || isFourthTrimester) && (
          <div
            className="mt-10 rounded-2xl border border-sienna/40 bg-cream p-6"
            data-testid="booking-locked-service"
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-sienna">
              You're reserving
            </p>
            <p className="mt-2 font-serif text-2xl leading-snug text-espresso">
              {service.title}
            </p>
            <p className="mt-1 text-sm text-cocoa/85">
              {service.duration} · {service.price}
            </p>
          </div>
        )}

        <div className="mt-12">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4 }}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="booking-service-list">
                  {services.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setService(s)}
                      data-testid={`booking-service-${s.slug}`}
                      className={`rounded-xl border p-6 text-left transition-colors ${
                        service?.id === s.id
                          ? "border-sienna bg-cream"
                          : "border-espresso/15 hover:border-espresso/40"
                      }`}
                    >
                      <h3 className="font-serif text-xl font-medium text-espresso">{s.title}</h3>
                      <p className="mt-1 text-sm text-cocoa">{s.tagline}</p>
                      <p className="mt-3 text-xs font-medium uppercase tracking-[0.15em] text-cocoa/70">
                        {s.duration} · {s.price}
                      </p>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => service && setStep(service.slug === FOURTH_TRIMESTER_SLUG ? 3 : 2)}
                  disabled={!service}
                  data-testid="booking-step1-next"
                  className="mt-8 flex items-center gap-2 rounded-full bg-sienna px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-sienna-dark disabled:opacity-40"
                >
                  Continue <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4 }}>
                <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                  <div>
                    <p className="mb-4 text-sm font-medium uppercase tracking-[0.15em] text-espresso">Choose a date</p>
                    <Popover open={calOpen} onOpenChange={setCalOpen}>
                      <PopoverTrigger asChild>
                        <button
                          data-testid="booking-date-trigger"
                          className="flex w-full items-center gap-3 rounded-lg border border-espresso/20 px-4 py-3 text-left text-sm text-espresso"
                        >
                          <CalendarIcon className="h-4 w-4 text-sienna" strokeWidth={1.5} />
                          {date ? format(date, "EEEE, MMMM d, yyyy") : "Select a date"}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto bg-ivory p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(d) => {
                            setDate(d);
                            setCalOpen(false);
                          }}
                          disabled={[{ before: new Date() }, (d) => !dayFilter(d)]}
                          defaultMonth={calendarFromDate}
                          fromDate={calendarFromDate}
                          toDate={calendarToDate}
                          data-testid="booking-calendar"
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="mt-3 text-xs leading-relaxed text-cocoa/70" data-testid="booking-days-note">
                      {daysNote}
                    </p>
                  </div>
                  <div>
                    <p className="mb-4 text-sm font-medium uppercase tracking-[0.15em] text-espresso">{wideSlots ? "Choose a window" : "Choose a time"}</p>
                    <div className={`grid gap-3 ${wideSlots ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-3"}`} data-testid="booking-time-slots">
                      {activeSlots.map((t) => {
                        const isTaken = date && taken.includes(t);
                        return (
                          <button
                            key={t}
                            onClick={() => !isTaken && setTime(t)}
                            disabled={isTaken}
                            data-testid={`booking-time-${t.replace(/[: ]/g, "-")}`}
                            className={`rounded-full border px-3 py-2.5 text-sm transition-colors ${
                              isTaken
                                ? "cursor-not-allowed border-espresso/10 text-cocoa/35 line-through"
                                : time === t
                                  ? "border-sienna bg-sienna text-ivory"
                                  : "border-espresso/20 text-espresso hover:border-espresso/50"
                            }`}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                    {date && taken.length > 0 && (
                      <p className="mt-3 text-xs text-cocoa/70" data-testid="taken-slots-note">
                        Crossed-out times are already booked for this day.
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-10 flex gap-4">
                  {!locked && (
                    <button
                      onClick={() => setStep(1)}
                      data-testid="booking-step2-back"
                      className="flex items-center gap-2 rounded-full border border-espresso/30 px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-espresso transition-colors hover:bg-espresso hover:text-ivory"
                    >
                      <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back
                    </button>
                  )}
                  <button
                    onClick={() => date && time && setStep(3)}
                    disabled={!date || !time}
                    data-testid="booking-step2-next"
                    className="flex items-center gap-2 rounded-full bg-sienna px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-sienna-dark disabled:opacity-40"
                  >
                    Continue <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4 }}>
                <div className="mb-8 rounded-xl border border-espresso/15 bg-cream p-6" data-testid="booking-review">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sienna">
                    {isFourthTrimester ? "Your six-week course" : "Your visit"}
                  </p>
                  {!(isFourthTrimester && locked) && <p className="mt-2 font-serif text-2xl text-espresso">{service.title}</p>}
                  {isFourthTrimester ? (
                    <CourseDates />
                  ) : (
                    <p className="mt-1 text-sm text-cocoa">
                      {format(date, "EEEE, MMMM d, yyyy")} at {time}
                    </p>
                  )}
                </div>

                <div
                  className="mb-8 rounded-xl border border-sienna/40 bg-sienna/10 p-6"
                  data-testid="payment-info-block"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-sienna">
                    A note on payment
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-espresso/90">
                    For simplicity and ease, <span className="font-serif italic text-espresso">cash payment and Bank Transfer / ACH</span> is
                    preferred. Credit and debit card are also available upon request.
                  </p>
                  {PAYMENT_PLAN_SLUGS.has(service.slug) && (
                    <p className="mt-3 text-sm leading-relaxed text-espresso/90" data-testid="payment-plan-note">
                      <span className="font-serif italic text-espresso">Payment plans</span> are available for this package.
                    </p>
                  )}
                  <p className="mt-4 text-xs leading-relaxed text-cocoa/80">
                    Once you send your enquiry, I'll reach out by email within the day to arrange
                    payment gently and privately — no card details are collected here.
                  </p>
                </div>
                <form onSubmit={submit} className="space-y-5" data-testid="booking-details-form">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <input required placeholder="Full name" value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} className={inputCls} data-testid="booking-name-input" />
                    <input required type="email" placeholder="Email address" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} className={inputCls} data-testid="booking-email-input" />
                  </div>
                  <input placeholder="Phone (optional)" value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} className={inputCls} data-testid="booking-phone-input" />
                  <textarea rows={4} placeholder="Anything we should know — due date, birth story so far, older children, pets…" value={details.notes} onChange={(e) => setDetails({ ...details, notes: e.target.value })} className={inputCls} data-testid="booking-notes-input" />
                  <div className="flex gap-4 pt-2">
                    {!(isFourthTrimester && locked) && (
                    <button
                      type="button"
                      onClick={() => setStep(isFourthTrimester ? 1 : 2)}
                      data-testid="booking-step3-back"
                      className="flex items-center gap-2 rounded-full border border-espresso/30 px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-espresso transition-colors hover:bg-espresso hover:text-ivory"
                    >
                      <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back
                    </button>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="booking-submit-button"
                      className="rounded-full bg-sienna px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-sienna-dark disabled:opacity-40"
                    >
                      {submitting ? "Booking…" : isFourthTrimester ? "Reserve my place" : "Confirm booking"}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </main>
  );
}
